import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../../lib/cx.js";

export interface MenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface MenuProps {
  /** Contenido del botón que abre el menú. */
  trigger: ReactNode;
  triggerLabel: string;
  triggerClassName?: string;
  items: (MenuItem | "separator")[];
  align?: "start" | "end";
  header?: ReactNode;
}

/**
 * Menú desplegable WAI-ARIA (botón + role="menu"): Enter/Espacio/↓ abren y enfocan el
 * primer ítem, ↑/↓/Inicio/Fin navegan, Esc cierra y devuelve el foco, clic fuera cierra.
 */
export function Menu({ trigger, triggerLabel, triggerClassName, items, align = "end", header }: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const actionable = items.filter((i): i is MenuItem => i !== "separator");

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function focusItem(idx: number) {
    const enabled = actionable.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
    if (enabled.length === 0) return;
    const target = enabled[((idx % enabled.length) + enabled.length) % enabled.length];
    if (target !== undefined) itemRefs.current[target]?.focus();
  }

  function openAndFocus(first: boolean) {
    setOpen(true);
    requestAnimationFrame(() => focusItem(first ? 0 : -1));
  }

  function onButtonKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAndFocus(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openAndFocus(false);
    }
  }

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    const enabled = actionable.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
    const pos = enabled.indexOf(current);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusItem(pos + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusItem(pos - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusItem(-1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }

  let actionIdx = -1;
  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onButtonKey}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={triggerLabel}
          onKeyDown={onMenuKey}
          className={cx(
            "absolute z-40 mt-1 min-w-52 rounded-lg border border-border bg-surface py-1 shadow-pop",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {header && <div className="border-b border-border px-3 py-2 text-[13px]">{header}</div>}
          {items.map((it, i) => {
            if (it === "separator") return <div key={`sep-${i}`} role="separator" className="my-1 border-t border-border" />;
            actionIdx += 1;
            const idx = actionIdx;
            return (
              <button
                key={it.key}
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  buttonRef.current?.focus();
                  it.onSelect();
                }}
                className={cx(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm [&>svg]:size-4 [&>svg]:shrink-0",
                  "focus:bg-surface-2 focus:outline-none hover:bg-surface-2 disabled:opacity-45",
                  it.danger ? "text-danger" : "text-fg",
                )}
              >
                {it.icon}
                {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
